import { createIndexedPcbConnectivityMap } from "lib/util/create-indexed-pcb-connectivity-map"
import { getFullConnectivityMapFromCircuitJson } from "circuit-json-to-connectivity-map"
import { addStartAndEndPortIdsIfMissing } from "./add-start-and-end-port-ids-if-missing"
import { checkCopperPourShorts } from "./check-copper-pour-shorts"
import { checkPcbBusLengthSkew } from "./check-pcb-bus-length-skew"
import { consolidatePcbOverlapErrors } from "./consolidate-pcb-overlap-errors"
import { checkSameNameNetsAreConnected } from "./check-same-name-nets-are-connected"
import type { AnyCircuitElement } from "circuit-json"
import { checkAllPinsInComponentAreUnderspecified } from "./check-all-pins-in-component-are-underspecified"
import { checkConnectorAccessibleOrientation } from "./check-connector-accessible-orientation"
import { checkCopperToBoardEdgeClearance } from "./check-copper-to-board-edge-clearance"
import { checkCourtyardOverlap } from "./check-courtyard-overlap/checkCourtyardOverlap"
import { checkDifferentNetViaSpacing } from "./check-different-net-via-spacing"
import { checkEachPcbPortConnectedToPcbTraces } from "./check-each-pcb-port-connected-to-pcb-trace"
import { checkEachPcbTraceNonOverlapping } from "./check-each-pcb-trace-non-overlapping/check-each-pcb-trace-non-overlapping"
import { checkNoGroundPinDefined } from "./check-no-ground-pin-defined"
import { checkNoPowerPinDefined } from "./check-no-power-pin-defined"
import { checkPadPadClearance } from "./check-pad-pad-clearance"
import { checkPadTraceClearance } from "./check-pad-trace-clearance"
import { checkPcbComponentOverCutout } from "./check-pcb-component-over-cutout"
import { checkPcbComponentsMissingCourtyard } from "./check-pcb-components-missing-courtyard"
import { checkPcbComponentsOutOfBoard } from "./check-pcb-components-out-of-board/checkPcbComponentsOutOfBoard"
import { checkPcbComponentOverlap } from "./check-pcb-components-overlap/checkPcbComponentOverlap"
import { checkPcbCopperOverKeepout } from "./check-pcb-copper-over-keepout"
import { checkPcbTraceLengths } from "./check-pcb-trace-lengths"
import { checkPcbTraceViaCounts } from "./check-pcb-trace-via-counts"
import { checkPinMustBeConnected } from "./check-pin-must-be-connected"
import { checkSameNetViaSpacing } from "./check-same-net-via-spacing"
import { checkSchematicComponentExcessiveVerticalPadding } from "./check-schematic-component-excessive-vertical-padding"
import { checkSchematicComponentMissingReferenceDesignatorText } from "./check-schematic-component-missing-reference-designator-text"
import { checkSchematicComponentPortsOutsideBody } from "./check-schematic-component-ports-outside-body"
import { checkSourceTracesHavePcbTraces } from "./check-source-traces-have-pcb-traces"
import { checkTestPointAccessibility } from "./check-testpoint-accessibility"
import { checkPcbTracesOutOfBoard } from "./check-trace-out-of-board/checkTraceOutOfBoard"
import { checkTracesAreContiguous } from "./check-traces-are-contiguous/check-traces-are-contiguous"
import { checkTwoTerminalSwitchContactsOnDifferentNets } from "./check-two-terminal-switch-contacts-on-different-nets"
import { checkViaPadClearance } from "./check-via-pad-clearance"
import { checkViaTraceClearance } from "./check-via-trace-clearance"
import { checkViasInPads } from "./check-vias-in-pads"

export async function runAllPlacementChecks(
  circuitJson: AnyCircuitElement[],
  { consolidateOverlaps = true } = {},
) {
  const errors = [
    ...checkCopperToBoardEdgeClearance(circuitJson),
    ...checkViasInPads(circuitJson),
    ...checkPcbComponentsOutOfBoard(circuitJson),
    ...checkPcbComponentOverCutout(circuitJson),
    ...checkPcbCopperOverKeepout(circuitJson),
    ...checkPcbComponentOverlap(circuitJson),
    ...checkPcbComponentsMissingCourtyard(circuitJson),
    ...checkPadPadClearance(circuitJson),
    ...checkCourtyardOverlap(circuitJson),
    ...checkConnectorAccessibleOrientation(circuitJson),
    ...checkTestPointAccessibility(circuitJson),
  ]
  return consolidateOverlaps
    ? consolidatePcbOverlapErrors(circuitJson, errors)
    : errors
}

export async function runAllNetlistChecks(circuitJson: AnyCircuitElement[]) {
  return [
    ...checkPinMustBeConnected(circuitJson),
    ...checkSameNameNetsAreConnected(circuitJson),
    ...checkTwoTerminalSwitchContactsOnDifferentNets(circuitJson),
  ]
}

export async function runAllSchematicChecks(circuitJson: AnyCircuitElement[]) {
  return [
    ...checkSchematicComponentExcessiveVerticalPadding(circuitJson),
    ...checkSchematicComponentMissingReferenceDesignatorText(circuitJson),
    ...checkSchematicComponentPortsOutsideBody(circuitJson),
  ]
}

export async function runAllPinSpecificationChecks(
  circuitJson: AnyCircuitElement[],
) {
  return [
    ...checkAllPinsInComponentAreUnderspecified(circuitJson),
    ...checkNoPowerPinDefined(circuitJson),
    ...checkNoGroundPinDefined(circuitJson),
  ]
}

export async function runAllRoutingChecks(circuitJson: AnyCircuitElement[]) {
  // Infer endpoint IDs before taking connectivity snapshots. Keep these maps
  // local to this pass so edits to the same circuit array cannot leave stale data.
  addStartAndEndPortIdsIfMissing(circuitJson)
  const connectivity = {
    connMap: getFullConnectivityMapFromCircuitJson(circuitJson),
    pcbConnectivityMap: createIndexedPcbConnectivityMap(circuitJson),
  }
  return [
    ...checkEachPcbPortConnectedToPcbTraces(circuitJson, connectivity),
    ...checkSourceTracesHavePcbTraces(circuitJson, connectivity),
    ...checkPcbTraceLengths(circuitJson),
    ...checkPcbBusLengthSkew(circuitJson),
    ...checkPcbTraceViaCounts(circuitJson),
    ...checkEachPcbTraceNonOverlapping(circuitJson, connectivity),
    ...checkCopperPourShorts(circuitJson, connectivity),
    ...checkPadTraceClearance(circuitJson, connectivity),
    ...checkViaTraceClearance(circuitJson, connectivity),
    ...checkViaPadClearance(circuitJson, connectivity),
    ...checkSameNetViaSpacing(circuitJson, connectivity),
    ...checkDifferentNetViaSpacing(circuitJson, connectivity),
    ...checkTracesAreContiguous(circuitJson, connectivity),
    ...checkPcbTracesOutOfBoard(circuitJson),
  ]
}

export async function runAllChecks(circuitJson: AnyCircuitElement[]) {
  return [
    ...(await runAllPlacementChecks(circuitJson)),
    ...(await runAllSchematicChecks(circuitJson)),
    ...(await runAllNetlistChecks(circuitJson)),
    ...(await runAllPinSpecificationChecks(circuitJson)),
    ...(await runAllRoutingChecks(circuitJson)),
  ]
}
